import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import { stopLenis, startLenis } from '@/lib/lenis';

type Side = 'right' | 'left' | 'bottom';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side?: Side;
  /** Width on right/left, height on bottom. Tailwind class. */
  size?: string;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

const variants = {
  right: {
    initial: { x: '100%' },
    animate: { x: 0 },
    exit:    { x: '100%' },
  },
  left: {
    initial: { x: '-100%' },
    animate: { x: 0 },
    exit:    { x: '-100%' },
  },
  bottom: {
    initial: { y: '100%' },
    animate: { y: 0 },
    exit:    { y: '100%' },
  },
} satisfies Record<Side, Record<'initial' | 'animate' | 'exit', { x?: string | number; y?: string | number }>>;

const layoutClasses: Record<Side, string> = {
  right: 'top-0 right-0 h-full',
  left:  'top-0 left-0 h-full',
  bottom: 'inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl',
};

export function Drawer({
  isOpen,
  onClose,
  side = 'right',
  size,
  children,
  className,
  ariaLabel = 'Side panel',
}: DrawerProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    stopLenis();
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      startLenis();
    };
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  const defaultSize =
    side === 'bottom' ? 'h-auto w-full' :
    'w-[min(420px,100vw)]';

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[1000]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-[color:var(--color-bg-deep)]/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-hidden
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className={cn(
              'absolute glass-card glass-card-strong overflow-y-auto',
              layoutClasses[side],
              size ?? defaultSize,
              className
            )}
            variants={variants[side]}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
